import React, { useState } from 'react';
import './AfricaHealthServices.css';
import { supabase } from '../supabase';

const AfricaHealthServices = () => {
  const [formData, setFormData] = useState({
    // Organization Information
    organizationName: '',
    organizationType: '',
    registrationNumber: '',
    yearEstablished: '',
    // Contact Information
    primaryContactName: '',
    primaryContactPhone: '',
    primaryContactEmail: '',
    organizationAddress: '',
    city: '',
    state: '',
    country: '',
    // Executive Details
    executiveName: '',
    executiveTitle: '',
    executivePhone: '',
    executiveEmail: '',
    // Project Information
    projectTitle: '',
    projectLocation: '',
    projectDuration: '',
    targetPopulation: '',
    projectDescription: '',
    // Health Focus
    healthFocus: [],
    // Distribution & Logistics
    distributionMethod: '',
    storageFacility: '',
    // Funding & Compliance
    fundingSource: '',
    hasCustomsExperience: '',
    hasTrackingCapacity: '',
    hasFunding: '',
    // Agreement
    agreeTerms: false
  });

  const [formStatus, setFormStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState({ title: '', message: '', type: '' });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      if (name === 'healthFocus') {
        const updated = checked
          ? [...formData.healthFocus, value]
          : formData.healthFocus.filter(item => item !== value);
        setFormData({ ...formData, healthFocus: updated });
      } else {
        setFormData({ ...formData, [name]: checked });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const validateForm = () => {
    const required = [
      'organizationName', 'organizationType', 'primaryContactName',
      'primaryContactPhone', 'primaryContactEmail', 'organizationAddress',
      'city', 'country', 'executiveName', 'executiveTitle',
      'projectTitle', 'projectLocation', 'projectDescription',
      'distributionMethod', 'storageFacility', 'fundingSource'
    ];

    for (const field of required) {
      if (!formData[field]) return false;
    }

    if (formData.healthFocus.length === 0) return false;
    if (!formData.agreeTerms) return false;

    return true;
  };

  const closePopup = () => {
    setShowPopup(false);
    if (formStatus === 'success') {
      setFormData({
        organizationName: '',
        organizationType: '',
        registrationNumber: '',
        yearEstablished: '',
        primaryContactName: '',
        primaryContactPhone: '',
        primaryContactEmail: '',
        organizationAddress: '',
        city: '',
        state: '',
        country: '',
        executiveName: '',
        executiveTitle: '',
        executivePhone: '',
        executiveEmail: '',
        projectTitle: '',
        projectLocation: '',
        projectDuration: '',
        targetPopulation: '',
        projectDescription: '',
        healthFocus: [],
        distributionMethod: '',
        storageFacility: '',
        fundingSource: '',
        hasCustomsExperience: '',
        hasTrackingCapacity: '',
        hasFunding: '',
        agreeTerms: false
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFormStatus(null);

    if (!validateForm()) {
      setPopupMessage({
        title: '⚠️ Incomplete Form',
        message: 'Please fill in all required fields before submitting.',
        type: 'error'
      });
      setShowPopup(true);
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase
        .from('health_services_applications')
        .insert([{
          organization_name: formData.organizationName,
          organization_type: formData.organizationType,
          registration_number: formData.registrationNumber,
          year_established: formData.yearEstablished,
          primary_contact_name: formData.primaryContactName,
          primary_contact_phone: formData.primaryContactPhone,
          primary_contact_email: formData.primaryContactEmail,
          organization_address: formData.organizationAddress,
          city: formData.city,
          state: formData.state,
          country: formData.country,
          executive_name: formData.executiveName,
          executive_title: formData.executiveTitle,
          executive_phone: formData.executivePhone,
          executive_email: formData.executiveEmail,
          project_title: formData.projectTitle,
          project_location: formData.projectLocation,
          project_duration: formData.projectDuration,
          target_population: formData.targetPopulation,
          project_description: formData.projectDescription,
          health_focus: formData.healthFocus.join(', '),
          distribution_method: formData.distributionMethod,
          storage_facility: formData.storageFacility,
          funding_source: formData.fundingSource,
          has_customs_experience: formData.hasCustomsExperience,
          has_tracking_capacity: formData.hasTrackingCapacity,
          has_funding: formData.hasFunding,
          status: 'pending'
        }]);

      if (error) throw error;

      setFormStatus('success');
      setPopupMessage({
        title: '✅ Application Submitted!',
        message: 'Your health services application has been received. Our team will contact you within 48 hours.',
        type: 'success'
      });
      setShowPopup(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormStatus('error');
      setPopupMessage({
        title: '❌ Submission Failed',
        message: 'There was a problem submitting your application. Please try again or contact us directly.',
        type: 'error'
      });
      setShowPopup(true);
    } finally {
      setLoading(false);
    }
  };

  const healthFocusOptions = [
    'Primary Care/Health Screening',
    'Secondary/Tertiary Health Care',
    'Maternal and Child Health',
    'Infectious Diseases (HIV, TB, etc.)',
    'Non-Communicable Diseases',
    'Nutrition/Feeding',
    'Clean Water/Sanitation',
    'Pharmacy/Medicines Distribution',
    'Community Health Education',
    'Training Local Health Workers',
    'Disaster Relief/Refugee Care',
    'Other'
  ];

  return (
    <div className="health-services-page">
      {/* POPUP MODAL */}
      {showPopup && (
        <div className="popup-overlay" onClick={closePopup}>
          <div className={`popup-content ${popupMessage.type}`} onClick={(e) => e.stopPropagation()}>
            <div className="popup-icon">
              {popupMessage.type === 'success' ? '✅' : '❌'}
            </div>
            <h2>{popupMessage.title}</h2>
            <p>{popupMessage.message}</p>
            <button className="popup-btn" onClick={closePopup}>
              {popupMessage.type === 'success' ? 'Continue' : 'Try Again'}
            </button>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="health-hero">
        <div className="health-hero-overlay"></div>
        <div className="health-hero-content">
          <span className="health-badge">ABVIG Health Services</span>
          <h1>Department of Africa Health & Services</h1>
          <p className="health-subtitle">
            Partnering with ASPI, Federal Ministry of Health Nigeria, Ministry of Health Ghana, 
            and International Health Partners including USAID, Project C.U.R.E., MAP International, WFP
          </p>
          <p className="health-description">
            Providing hospital facility refurbishment, medical equipment, health education services, 
            and project development for communities across Africa.
          </p>
          <button className="btn-gold" onClick={() => document.getElementById('health-form').scrollIntoView({ behavior: 'smooth' })}>
            Apply for Partnership
          </button>
        </div>
      </section>

      {/* Services Overview */}
      <section className="health-services-section">
        <div className="container">
          <h2 className="section-title">Our Health Services</h2>
          <div className="health-services-grid">
            <div className="health-service-card">
              <span className="health-service-icon">🏥</span>
              <h3>Hospital Facility Refurbishment</h3>
              <p>Renovation and upgrading of healthcare facilities to meet modern standards.</p>
            </div>
            <div className="health-service-card">
              <span className="health-service-icon">💊</span>
              <h3>Medical Equipment & Supplies</h3>
              <p>Provision of essential medicines, equipment, and post-COVID-19 supplies.</p>
            </div>
            <div className="health-service-card">
              <span className="health-service-icon">📚</span>
              <h3>Health Education Services</h3>
              <p>Community health education and training programs for healthcare workers.</p>
            </div>
            <div className="health-service-card">
              <span className="health-service-icon">📋</span>
              <h3>Project Development & Management</h3>
              <p>End-to-end project management for health infrastructure and programs.</p>
            </div>
            <div className="health-service-card">
              <span className="health-service-icon">🛡️</span>
              <h3>HSE & Safety Services</h3>
              <p>Health, safety, and environmental compliance for healthcare operations.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="health-form" className="health-form-section">
        <div className="container">
          <div className="health-form-wrapper">
            <div className="health-form-header">
              <h2>Health Services Partnership Application</h2>
              <p>
                Complete this form to apply for ABVIG health services partnership. 
                All fields marked with * are mandatory.
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Section 1: Organization Information */}
              <div className="form-section-title">
                <h3>1. ORGANIZATION INFORMATION</h3>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Organization Name *</label>
                  <input
                    type="text"
                    name="organizationName"
                    value={formData.organizationName}
                    onChange={handleChange}
                    required
                    placeholder="Enter organization name"
                  />
                </div>
                <div className="form-group">
                  <label>Organization Type *</label>
                  <select
                    name="organizationType"
                    value={formData.organizationType}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select type</option>
                    <option value="NGO">NGO / Non-Profit</option>
                    <option value="Hospital">Hospital / Health Facility</option>
                    <option value="Government">Government Agency</option>
                    <option value="Faith-Based">Faith-Based Organization</option>
                    <option value="Community">Community-Based Organization</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Registration Number</label>
                  <input
                    type="text"
                    name="registrationNumber"
                    value={formData.registrationNumber}
                    onChange={handleChange}
                    placeholder="Enter registration number"
                  />
                </div>
                <div className="form-group">
                  <label>Year Established</label>
                  <input
                    type="text"
                    name="yearEstablished"
                    value={formData.yearEstablished}
                    onChange={handleChange}
                    placeholder="e.g., 2010"
                  />
                </div>
              </div>

              {/* Section 2: Contact Information */}
              <div className="form-section-title">
                <h3>2. CONTACT INFORMATION</h3>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Primary Contact Name *</label>
                  <input
                    type="text"
                    name="primaryContactName"
                    value={formData.primaryContactName}
                    onChange={handleChange}
                    required
                    placeholder="Full name"
                  />
                </div>
                <div className="form-group">
                  <label>Primary Contact Phone *</label>
                  <input
                    type="tel"
                    name="primaryContactPhone"
                    value={formData.primaryContactPhone}
                    onChange={handleChange}
                    required
                    placeholder="+234 800 000 0000"
                  />
                </div>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Primary Contact Email *</label>
                  <input
                    type="email"
                    name="primaryContactEmail"
                    value={formData.primaryContactEmail}
                    onChange={handleChange}
                    required
                    placeholder="email@organization.org"
                  />
                </div>
                <div className="form-group">
                  <label>Organization Address *</label>
                  <input
                    type="text"
                    name="organizationAddress"
                    value={formData.organizationAddress}
                    onChange={handleChange}
                    required
                    placeholder="Street address"
                  />
                </div>
              </div>

              <div className="form-row three-col">
                <div className="form-group">
                  <label>City *</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    placeholder="City"
                  />
                </div>
                <div className="form-group">
                  <label>State/Province</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    placeholder="State"
                  />
                </div>
                <div className="form-group">
                  <label>Country *</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                    placeholder="Country"
                  />
                </div>
              </div>

              {/* Section 3: Executive Details */}
              <div className="form-section-title">
                <h3>3. EXECUTIVE DETAILS</h3>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Executive Name *</label>
                  <input
                    type="text"
                    name="executiveName"
                    value={formData.executiveName}
                    onChange={handleChange}
                    required
                    placeholder="Full name"
                  />
                </div>
                <div className="form-group">
                  <label>Executive Title *</label>
                  <input
                    type="text"
                    name="executiveTitle"
                    value={formData.executiveTitle}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Executive Director"
                  />
                </div>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Executive Phone</label>
                  <input
                    type="tel"
                    name="executivePhone"
                    value={formData.executivePhone}
                    onChange={handleChange}
                    placeholder="+234 800 000 0000"
                  />
                </div>
                <div className="form-group">
                  <label>Executive Email</label>
                  <input
                    type="email"
                    name="executiveEmail"
                    value={formData.executiveEmail}
                    onChange={handleChange}
                    placeholder="executive@organization.org"
                  />
                </div>
              </div>

              {/* Section 4: Project Information */}
              <div className="form-section-title">
                <h3>4. PROJECT INFORMATION</h3>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Project Title *</label>
                  <input
                    type="text"
                    name="projectTitle"
                    value={formData.projectTitle}
                    onChange={handleChange}
                    required
                    placeholder="Enter project title"
                  />
                </div>
                <div className="form-group">
                  <label>Project Location *</label>
                  <input
                    type="text"
                    name="projectLocation"
                    value={formData.projectLocation}
                    onChange={handleChange}
                    required
                    placeholder="City, Country"
                  />
                </div>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Project Duration</label>
                  <input
                    type="text"
                    name="projectDuration"
                    value={formData.projectDuration}
                    onChange={handleChange}
                    placeholder="e.g., 12 months"
                  />
                </div>
                <div className="form-group">
                  <label>Target Population</label>
                  <input
                    type="text"
                    name="targetPopulation"
                    value={formData.targetPopulation}
                    onChange={handleChange}
                    placeholder="Estimated number of beneficiaries"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Project Description *</label>
                <textarea
                  name="projectDescription"
                  value={formData.projectDescription}
                  onChange={handleChange}
                  required
                  rows="4"
                  placeholder="Describe your project, its goals, and expected impact..."
                />
              </div>

              {/* Section 5: Health Focus */}
              <div className="form-section-title">
                <h3>5. HEALTH FOCUS AREAS (Select all that apply) *</h3>
              </div>

              <div className="checkbox-grid">
                {healthFocusOptions.map((option) => (
                  <label className="checkbox-label" key={option}>
                    <input
                      type="checkbox"
                      name="healthFocus"
                      value={option}
                      checked={formData.healthFocus.includes(option)}
                      onChange={handleChange}
                    />
                    {option}
                  </label>
                ))}
              </div>

              {/* Section 6: Distribution & Logistics */}
              <div className="form-section-title">
                <h3>6. DISTRIBUTION & LOGISTICS</h3>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Distribution Method *</label>
                  <select
                    name="distributionMethod"
                    value={formData.distributionMethod}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select method</option>
                    <option value="Direct to Facility">Direct to Health Facility</option>
                    <option value="In-Country Distribution">In-Country Distribution Center</option>
                    <option value="Mobile Clinic">Mobile Clinic</option>
                    <option value="Community Outreach">Community Outreach</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Storage Facility *</label>
                  <select
                    name="storageFacility"
                    value={formData.storageFacility}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select option</option>
                    <option value="Secure Warehouse">Secure Warehouse</option>
                    <option value="Health Facility Storage">Health Facility Storage</option>
                    <option value="Temperature Controlled">Temperature Controlled</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              {/* Section 7: Funding & Compliance */}
              <div className="form-section-title">
                <h3>7. FUNDING & COMPLIANCE</h3>
              </div>

              <div className="form-group">
                <label>Funding Source *</label>
                <input
                  type="text"
                  name="fundingSource"
                  value={formData.fundingSource}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Donations, Grants, Government Funding"
                />
              </div>

              <div className="form-row three-col">
                <div className="form-group">
                  <label>Customs Experience</label>
                  <select
                    name="hasCustomsExperience"
                    value={formData.hasCustomsExperience}
                    onChange={handleChange}
                  >
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Tracking Capacity</label>
                  <select
                    name="hasTrackingCapacity"
                    value={formData.hasTrackingCapacity}
                    onChange={handleChange}
                  >
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Funding Available</label>
                  <select
                    name="hasFunding"
                    value={formData.hasFunding}
                    onChange={handleChange}
                  >
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>
              </div>

              {/* Section 8: Agreement */}
              <div className="form-section-title">
                <h3>8. AGREEMENT & DECLARATION</h3>
              </div>

              <div className="declaration-text">
                <p>
                  I/We declare that all information provided is true, accurate and complete. 
                  I/We understand that ABVIG may verify the information provided.
                </p>
                <p>
                  I/We agree that all donated medicines and medical supplies received through ABVIG 
                  will be used exclusively for charitable work and will not be sold, exchanged, or 
                  re-exported in violation of applicable laws.
                </p>
                <p>
                  I/We agree to comply with all applicable laws and regulations, including the 
                  U.S. Foreign Corrupt Practices Act (FCPA), OFAC sanctions, and BIS regulations.
                </p>
                <p>
                  I/We agree to submit distribution and impact reports as required by ABVIG and its partners.
                </p>
                <p>
                  I/We confirm that I/We have read and understood this declaration.
                </p>
              </div>

              <div className="form-group checkbox">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="agreeTerms"
                    checked={formData.agreeTerms}
                    onChange={handleChange}
                    required
                  />
                  I confirm that I have read and understood all terms and conditions. *
                </label>
              </div>

              <button type="submit" className="btn-gold submit-btn" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Application'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AfricaHealthServices;